import React, { useState, FC } from 'react';
import linkifyStr from 'linkifyjs/string';
import parse from 'html-react-parser';
import { sendMessage } from '../controller/client/PartyClient';
import { Input, Form } from 'antd';
import {
  IPartyForUser,
  TUserId,
  IMessage,
  TMessageId,
  IParticipantForUser,
} from '../PartyClientResponse';
import autoscroll from 'autoscroll-react';
import { getIdToken } from '../controller/PartyDB';
import { useAppState } from '../state/AppState';
import { IUser } from '../Party';

interface IParticipantDirectMessagesInfo {
  targetId: TUserId;
  targetName: string;
  participant?: IParticipantForUser;
  messages: IMessage[];
}

const DMParticipantRow = ({
  info,
  setSelectedParticipant,
}: {
  info: IParticipantDirectMessagesInfo;
  setSelectedParticipant: (p: IParticipantForUser | null) => void;
}) => {
  const { user } = useAppState();
  const { participant } = info;
  if (!participant) {
    return <div />;
  }

  return (
    <div
      className='participant-list-item'
      style={{ cursor: 'pointer' }}
      key={participant.uid}
      onClick={() => {
        setSelectedParticipant(participant);
      }}>
      <div className='participant-list-name'>
        <img
          src={participant.photoURL}
          alt={participant.displayName}
          width={24}
          height={24}
          style={{
            margin: '4px 16px 4px 0',
            objectFit: 'cover',
            borderRadius: '50%',
          }}
        />
        {participant.displayName}
      </div>
    </div>
  );
};

class DirectMessageList extends React.Component<{
  items: (IMessage & { id: string })[];
  user: IUser;
}> {
  render() {
    const { items, user, ...props } = this.props;
    return (
      <div className='area-chat' {...props}>
        {this.props.items.map((message) => {
          return (
            <div
              style={{
                padding: 4,
                display: 'flex',
                alignItems: 'center',
              }}
              key={message.id}>
              <img
                style={{
                  margin: '4px 8px 4px 0',
                  alignSelf: 'flex-start',
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                }}
                src={message.creatorPhotoUrl}
                alt='avatar'
              />
              <span
                className='message-sender'
                style={{
                  marginRight: '1em',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  alignSelf: 'flex-start',
                }}>
                {message.creatorName}
              </span>
              <span>
                {parse(
                  linkifyStr(message.text, {
                    defaultProtocol: 'https',
                    className: 'message-link',
                    tagName: 'a',
                  })
                )}
              </span>
            </div>
          );
        })}
      </div>
    );
  }
}

const ScrollableDirectMessages = autoscroll(DirectMessageList);

interface Props {
  messageInfo: (IMessage & { id: string }) | any;
  user: IUser | null;
}

const DirectMessagesHeader: FC<Props> = ({ messageInfo, user }) => {
  const isCurrentUserCreator = user?.uid === messageInfo?.creatorId;
  const companionUserName = isCurrentUserCreator
    ? messageInfo?.targetUserDisplayName
    : messageInfo?.creatorName;
  const companionUserPhoto = isCurrentUserCreator
    ? messageInfo?.targetUserAvatar
    : messageInfo?.creatorPhotoUrl;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: '0 0 5px',
      }}>
      <div
        style={{
          margin: '0 0 5px',
          width: 60,
          height: 60,
          borderRadius: '50%',
          overflow: 'hidden',
        }}>
        <img
          style={{
            height: '100%',
            position: 'relative',
            transform: 'translateX(-50%)',
            left: '50%',
          }}
          src={companionUserPhoto}
          alt='avatar'
        />
      </div>
      <span
        style={{
          fontWeight: 'bold',
          margin: '0 0 10px',
          whiteSpace: 'nowrap',
        }}>
        {companionUserName}
      </span>
    </div>
  );
};

const DirectMessages = ({
  party,
  setSelectedParticipant,
  selectedParticipant,
}: {
  party: IPartyForUser;
  setSelectedParticipant: (p: IParticipantForUser | null) => void;
  selectedParticipant: IParticipantForUser | null;
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const { user } = useAppState();
  const [eagerMessages, setEagerMessages] = useState<Partial<{ [key in TMessageId]: IMessage }>>(
    {}
  );

  const [form] = Form.useForm();

  const directMessages = [
    ...Object.entries(eagerMessages || {})
      .filter(
        (m) => !Object.values(party.messages || {}).find((msg) => msg?.localId === m[1]?.localId)
      )
      .map((m) => ({
        ...m[1],
        id: m[1]?.localId,
      })),
    ...Object.entries(party.messages || {}).map((m) => ({
      ...m[1],
      id: eagerMessages[m[0] as TMessageId]?.localId || m[0],
    })),
  ]
    // @ts-ignore
    .sort((a, b) => (a.createdAt > b.createdAt ? 1 : -1))
    // .filter((_, i, fullList) => i >= fullList.length - MAX_MESSAGES_SHOWN);
    // uncomment this to scope messages to areas again (also update partycontroller)
    .filter(
      (message) => message.scope && (message.scope === user?.uid || message.creatorId === user?.uid)
    );

  const conversationList = directMessages.reduce((acc, message) => {
    if (message.creatorId === user?.uid) {
      const targetId = message.scope || ('' as TUserId);
      const participant = party.stage.participants[targetId as TUserId];
      return {
        ...acc,
        [targetId]: {
          targetId,
          targetName: 'test',
          participant,
          messages: [...(acc[targetId]?.messages || []), message],
        },
      };
    }

    if (message.scope === user?.uid && user?.uid !== undefined && message.scope !== undefined) {
      const targetId = message.creatorId || ('' as TUserId);
      const participant = party.stage.participants[targetId as TUserId];
      return {
        ...acc,
        [targetId]: {
          targetId,
          targetName: participant.displayName,
          participant,
          messages: [...(acc[targetId]?.messages || []), message],
        },
      };
    }
    return {
      ...acc,
    };
  }, {} as any);

  Object.values(party.stage.participants).forEach((p) => {
    if (!conversationList[p.uid]) {
      conversationList[p.uid] = {
        targetId: p.uid,
        targetName: p.displayName,
        messages: [],
      };
    }
  });

  if (!selectedParticipant) {
    return (
      <div className='participant-list'>
        {Object.values(conversationList).map((convo) => (
          <DMParticipantRow
            info={convo as IParticipantDirectMessagesInfo}
            setSelectedParticipant={setSelectedParticipant}
          />
        ))}
      </div>
    );
  }

  return (
    <div className='dm-container'>
      <div style={{ display: 'flex', padding: '6px' }}>
        <a onClick={() => setSelectedParticipant(null)}>{`< Return`}</a>
      </div>
      <div className='chat-container'>
        <DirectMessagesHeader
          messageInfo={(conversationList?.[selectedParticipant.uid]?.messages || [])?.[0]}
          user={user}
        />
        <ScrollableDirectMessages
          items={conversationList?.[selectedParticipant.uid]?.messages || []}
          user={user}
        />
        <Form
          form={form}
          style={{
            display: 'flex',
            flexGrow: 0,
            maxHeight: 60,
            justifySelf: 'flex-end',
          }}
          name='message-input'
          onFinish={async () => {
            setInputValue('');
            const localId = String(100000 * Math.random()) as TMessageId;
            try {
              setEagerMessages((messages) => ({
                ...messages,
                [localId]: {
                  creatorId: user?.uid || ('' as TUserId),
                  creatorName: user?.displayName || '',
                  creatorPhotoUrl: user?.avatarUrl || user?.photoURL || '',
                  text: inputValue,
                  scope: null,
                  createdAt: Date.now(),
                  localId,
                },
              }));
              await sendMessage({
                idToken: await getIdToken(),
                partyKey: party.key,
                text: inputValue,
                localId,
                targetId: selectedParticipant?.uid,
              });
            } catch (err) {
              setEagerMessages(({ [localId]: msgToRemove, ...messages }) => messages);
              console.error(err);
            }
          }}>
          <div style={{ display: 'flex', width: '100%' }}>
            <Input
              className='chat-input'
              value={inputValue}
              autoFocus
              onChange={(e) => setInputValue(e.target.value)}
              placeholder='Send a message to your room'
            />
            <img
              alt='Send'
              className='chat-send'
              width={40}
              src='/icons/Send.svg'
              onClick={() => form.submit()}></img>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default DirectMessages;
